import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import LogoFullImage from '../assets/Voice victor_png_logo_854x854.png';
import LogoLineImage from '../assets/Voice victor_png_logo_no_star_854x854.png';

// Animation der tegner linjen fra venstre til højre
const drawLineAnimation = keyframes`
  0% {
    clip-path: inset(0 100% 0 0);
  }
  100% {
    clip-path: inset(0 0 0 0);
  }
`;

// Container for loading screen
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${props => props.isFading && !props.inline ? 0 : 1};
  transition: ${props => !props.inline ? 'opacity 0.8s ease-out' : 'none'};

  ${props => !props.inline && `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #000000; /* Sort baggrund for fuldskærm */
    z-index: 9999;
  `}

  ${props => props.inline && `
    background-color: transparent; /* Gennemsigtig baggrund for inline */
    /* Forældrekomponent håndterer positionering og overordnet størrelse for inline loaderen */
  `}

  /* Alle børneelementer skal have samme opacity som parent i fuldskærmstilstand */
  ${props => !props.inline && `
    & * {
      transition: opacity 0.8s ease-out;
    }
  `}
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  /* Opacity arves fra LoadingContainer for at sikre synkron fade ved fuldskærm */
  opacity: inherit; 

  width: ${props => props.inline && props.inlineSize ? props.inlineSize.width : '300px'};
  height: ${props => props.inline && props.inlineSize ? props.inlineSize.height : '150px'};
`;

// Logo billede (enten linje eller fuldt logo)
const LogoImg = styled.img`
  max-width: 100%;
  height: auto;
  opacity: inherit; /* Arv opacity for synkron fade i fuldskærm */
  filter: contrast(1.05) brightness(1.02);
  image-rendering: -webkit-optimize-contrast;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  transform: translateZ(0);
  -webkit-font-smoothing: subpixel-antialiased;
`;

// Animeret linje-logo med forbedret skarphed
const AnimatedLineLogo = styled(LogoImg)`
  clip-path: inset(0 100% 0 0);
  animation: ${drawLineAnimation} 0.9s ease-out forwards;
  filter: contrast(1.08) brightness(1.05) drop-shadow(0 0 0.3px rgba(255, 255, 255, 0.2));
`;

// Ekstra styling for fuldt logo med stjerne
const FullLogo = styled(LogoImg)`
  filter: contrast(1.08) brightness(1.05) drop-shadow(0 0 0.5px rgba(255, 255, 255, 0.3));
`;

const LoadingScreen = ({
  isVisible = true,
  inline = false,
  inlineSize = { width: '80px', height: '40px' } // Standard inline størrelse (ca. som en spinner)
}) => {
  const [showFullLogo, setShowFullLogo] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [shouldRender, setShouldRender] = useState(isVisible);

  useEffect(() => {
    if (inline) {
      // For inline-tilstand styres synlighed direkte, ingen kompleks fade/unmount logik
      setShouldRender(isVisible);
      if (isVisible) {
        setShowFullLogo(false); // Nulstil animationen, hvis den bliver synlig igen
      }
      return;
    }

    // Eksisterende logik for fuldskærmstilstand
    if (isVisible) {
      setShouldRender(true);
      setIsFading(false);
      setShowFullLogo(false); // Nulstil animationen
    } else {
      setIsFading(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 800); // Samme tid som transition-varighed
      return () => clearTimeout(timer);
    }
  }, [isVisible, inline]);

  // Nulstil animationen, når den bliver synlig igen i begge tilstande
  // Dette sikrer, at hvis en inline loader skjules og vises igen, starter animationen forfra.
  useEffect(() => {
    if (isVisible) {
      setShowFullLogo(false);
    }
  }, [isVisible]);

  if (!inline && !shouldRender) return null; // Fuldskærmsloader afmonteres efter fade
  if (inline && !isVisible) return null;   // Inline-loader skjules direkte

  return (
    <LoadingContainer isFading={isFading} inline={inline}>
      <LogoContainer inline={inline} inlineSize={inlineSize}>
        {!showFullLogo ? (
          <AnimatedLineLogo
            src={LogoLineImage}
            alt="Voice Victor Line Loading"
            onAnimationEnd={() => setShowFullLogo(true)}
          />
        ) : (
          <FullLogo src={LogoFullImage} alt="Voice Victor Logo Loading" />
        )}
      </LogoContainer>
    </LoadingContainer>
  );
};

export default LoadingScreen;